import { apiClient } from "@/lib/client";
import {
  BillingPlanIdentifierSchema,
  CurrentSubscribingPlanSchema,
  InvoiceSchema,
  PaymentMethodSchema,
  PaymentSchema
} from "@/types/payment-and-plan";
import { componentType } from "@/utils/component";
import { makeApi, makeErrors } from "@zodios/core";
import z from "zod";

export type GetPaymentDetailsRequestType = z.infer<typeof GetPaymentDetailsRequestSchema>;
export type CreateSubscriptionRequestType = z.infer<typeof CreateSubscriptionRequestSchema>;
export type CreateSubscriptionResponseType = z.infer<typeof CreateSubscriptionResponseSchema>;
export type CancelSubscriptionRequestType = z.infer<typeof CancelSubscriptionRequestSchema>;
export type CancelSubscriptionResponseType = z.infer<typeof CancelSubscriptionResponseSchema>;
export type SubscribePlanRequestType = z.infer<typeof SubscribePlanRequestSchema>;
export type AnnualToMonthlyPlanResponseType = z.infer<typeof AnnualToMonthlyPlanResponseSchema>;
export type SubscribePlanResponseType = z.infer<typeof SubscribePlanResponseSchema>;
export type MonthlyToAnnualPlanResponseType = z.infer<typeof MonthlyToAnnualPlanResponseSchema>;

export const GetPaymentDetailsRequestSchema = z.object({
  params: z.object({
    payment_id: z.string(),
    merchant_payment_id: z.string()
  })
});

export const CreateSubscriptionRequestSchema = z.object({
  coupon_code: z.string().nullable()
});

export const CreateSubscriptionResponseSchema = z.object({
  message: z.string()
});

export const CreateCheckoutSessionRequestSchema = z.object({
  coupon_code: z.string().nullable()
});

export const CreateCheckoutSessionResponseSchema = z.object({
  redirect_url: z.string()
});

export const CancelSubscriptionRequestSchema = z.object({
  reasons: z.string().array()
});

export const CancelSubscriptionResponseSchema = z.object({
  message: z.string()
});

export const SubscribePlanRequestSchema = z.object({
  plan_identifier: BillingPlanIdentifierSchema
});

export const SubscribePlanResponseSchema = z.object({
  message: z.string()
});

export const MonthlyToAnnualPlanResponseSchema = z.object({
  message: z.string()
});

export const AnnualToMonthlyPlanResponseSchema = z.object({
  message: z.string()
});

const errors = makeErrors([
  {
    status: 400,
    description: "Not subscribed to a plan",
    schema: z.object({
      code: z.string(),
      message: z.string()
    })
  },
  {
    status: 401,
    description: "Unauthorized",
    schema: z.object({
      code: z.string(),
      message: z.string()
    })
  }
]);

export const paymentAndPlanApi = makeApi([
  {
    alias: "getAllInvoices",
    method: "get",
    path: "/invoice",
    response: InvoiceSchema.array(),
    errors
  },
  {
    alias: "getCustomerPoralUrl",
    method: "get",
    path: "/subscription/customer_portal",
    response: z.object({
      redirect_url: z.string()
    }),
    errors
  },
  {
    alias: "getAllPaymentMethods",
    method: "get",
    path: "/payment_method",
    response: PaymentMethodSchema.array(),
    errors
  },
  {
    alias: "getPaymentDetails",
    method: "get",
    path: "/payment/detail",
    response: PaymentSchema.array(),
    parameters: [
      {
        name: "params",
        type: "Query",
        schema: GetPaymentDetailsRequestSchema
      }
    ],
    errors
  },
  {
    alias: "_getCurrentPlan",
    method: "get",
    path: "/enrollment_record/current_plan",
    response: CurrentSubscribingPlanSchema,
    errors
  },
  {
    alias: "createSubscription",
    method: "post",
    path: "/subscription",
    response: CreateSubscriptionResponseSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSubscriptionRequestSchema
      },
      {
        name: "plan_identifier",
        type: "Query",
        schema: BillingPlanIdentifierSchema
      }
    ],
    errors
  },
  {
    alias: "createCheckoutSession",
    method: "post",
    path: "/subscription/checkout_session",
    response: CreateCheckoutSessionResponseSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSubscriptionRequestSchema
      },
      {
        name: "plan_identifier",
        type: "Query",
        schema: BillingPlanIdentifierSchema
      }
    ],
    errors
  },
  {
    alias: "cancelSubscription",
    method: "post",
    path: "/subscription/unsubscribe",
    response: CancelSubscriptionResponseSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CancelSubscriptionRequestSchema
      }
    ],
    errors
  }
]);

export const _fetchCurrentPlan = async (cookie?: string | undefined) => {
  try {
    return await apiClient._getCurrentPlan({ headers: { cookie } });
  } catch (err) {
    console.error(err);
    return undefined;
  }
};
